import {
  DetailRequirement,
  EngagementTaskFileUploadParams,
  EngagementTaskTrait,
  FilesApi,
  FileShape,
  ResourceDataType,
} from '@cibo/core'
import { Markdown, useCreateFile, useSaveIndicator } from '@cibo/ui'
import { UI_QUERY_KEY } from '@cibo/ui/src/queries/queryKey'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useQueries, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import bytes from 'bytes'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { useUpdateEngagementTaskData } from '../../../../../queries/useUpdateEngagementTaskData'
import { TaskDataModel } from '../../../models/TaskDataModel'
import { AddExistingFileModal } from './AddExistingFileModal'
import { FilesTable } from './FilesTable'

const ACCEPTED_FILES = ['csv', 'xls', 'xls', 'pdf', 'jpg', 'png', 'txt']
const MAX_FILE_SIZE = 200 * 1024 * 1024

const resolveMaxSize = (params?: EngagementTaskFileUploadParams) => {
  const maxSize = params?.maxSize && bytes.parse(params?.maxSize)
  return typeof maxSize === 'number' ? maxSize : MAX_FILE_SIZE
}

const resolveExtensions = (params?: EngagementTaskFileUploadParams) => {
  return params?.extension ? (params.extension as unknown as string[]) : ACCEPTED_FILES
}

const DashedDropzone = styled(Button)(() => {
  const theme = useTheme()
  return {
    border: '2px dashed',
    borderColor: theme.palette.primary.main,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  }
})

type DataEntryFileUploadProps = {
  taskId: string
  traitConfig: EngagementTaskTrait<ResourceDataType>
  taskData: UseQueryResult<TaskDataModel, Error>
  detailRequirement?: DetailRequirement
}

export const DataEntryFileUpload = ({
  taskData,
  taskId,
  traitConfig,
  detailRequirement,
}: DataEntryFileUploadProps) => {
  const { t } = useTranslation(['@cibo/programs/DataEntryFileUpload'])
  const { enqueueSnackbar } = useSnackbar()
  const { setIsSaving, setIsError, setIsDirty } = useSaveIndicator()

  const [addExistingModal, setAddExistingModal] = useState<{ open: boolean }>({ open: false })

  const { engagementId, engagement, userId } = useProgramEngagementContext()

  const createFile = useCreateFile()
  const updateTaskData = useUpdateEngagementTaskData({
    engagementId,
    taskId,
  })

  const queryClient = useQueryClient()

  // @TODO: use the generic SingleTargetTraitEditor.handleSaveTraitDetail via prop
  const saveDetail = useCallback((fileRefs: string[]) => {
    setIsSaving(true)
    setIsError(false)

    const updateValue = [{ traitId: traitConfig.traitId, value: { fileResIds: fileRefs } }]

    updateTaskData
      .mutateAsync({
        updates:
          traitConfig.dataType === 'user'
            ? { userDetails: updateValue }
            : { wfDetails: updateValue },
      })
      .then(() => {
        // relationships on files have changed
        queryClient.invalidateQueries({
          queryKey: [UI_QUERY_KEY.ALL_FILES],
        })

        setIsSaving(false)
        setIsDirty(false)
      })
      .catch(error => {
        console.error(error)
        setIsSaving(false)
        setIsError(true)
      })
  }, [])

  const removeDetail = useCallback(() => {
    setIsSaving(true)
    setIsError(false)

    const updateValue = [{ traitId: traitConfig.traitId, value: null }]

    updateTaskData
      .mutateAsync({
        updates:
          traitConfig.dataType === 'user'
            ? { userDetails: updateValue }
            : { wfDetails: updateValue },
      })
      .then(() => {
        setIsSaving(false)
        setIsDirty(false)
      })
      .catch(error => {
        console.error(error)
        setIsSaving(false)
        setIsError(true)
      })
  }, [])

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params

  const [fileRefs, setFileRefs] = useState<string[]>()

  const detail = detailRequirement && taskData.data?.getDetail({ traitConfig, detailRequirement })
  const detailValue =
    detailRequirement && taskData.data?.getDetailValue({ traitConfig, detailRequirement })

  useEffect(() => {
    if (typeof fileRefs === 'undefined') {
      if (detailValue && Array.isArray(detailValue.fileResIds)) {
        setFileRefs(detailValue.fileResIds)
      } else {
        setFileRefs([])
      }
    }
  }, [detailValue])

  const files = useQueries({
    queries:
      fileRefs?.map(fileId => ({
        queryKey: ['file', fileId],
        queryFn: () => FilesApi.getFile(fileId).then(response => response.data.items[0]),
      })) || [],
  })

  const handleRemoveFile = useCallback((item: FileShape) => {
    setFileRefs(prev => {
      const newFileRefs = (prev || []).filter(fileId => fileId !== item.id)
      saveDetail(newFileRefs)
      return newFileRefs
    })
  }, [])

  const handleAddExisting = (item: FileShape) => {
    setFileRefs(prev => {
      const newFileRefs = [...(prev || []), item.id]
      saveDetail(newFileRefs)
      return newFileRefs
    })
  }

  const onDrop = useCallback(
    (acceptedFiles: Array<File>, rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        setIsSaving(true)

        Promise.all(
          acceptedFiles.map(file =>
            createFile.mutateAsync({ ownerId: userId, file, fileType: params?.fileType ?? taskId })
          )
        )
          .then(responses => {
            enqueueSnackbar(t('filesUploaded', { count: acceptedFiles.length }), {
              variant: 'success',
            })

            setFileRefs(prev => {
              const newFileRefs = [...(prev || []), ...responses.map(response => response.id)]
              saveDetail(newFileRefs)
              return newFileRefs
            })
          })
          .catch(error => {
            enqueueSnackbar(t('fileUploadError'), { variant: 'error' })
          })
          .finally(() => {
            setIsSaving(false)
          })
      }

      if (rejectedFiles.length > 0) {
        enqueueSnackbar(t('filesRejected', { count: rejectedFiles.length }), { variant: 'error' })
      }
    },
    [params]
  )

  const maxFiles: number = params?.maxFiles
    ? typeof params.maxFiles === 'string'
      ? parseInt(params.maxFiles, 10) || 1
      : params.maxFiles
    : 1

  const remainingFileCount = fileRefs ? maxFiles - (fileRefs.length || 0) : maxFiles

  const maxSize = resolveMaxSize(params)
  const extensions = resolveExtensions(params)

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: extensions.map(ext => `.${ext}`),
    maxSize: maxSize,
    noClick: true,
    maxFiles: remainingFileCount,
    multiple: maxFiles > 1,
  })

  const helperText =
    engagement.data && engagement.data.replaceCopyTokens((params as any)?.helperTextMd)

  const immutable = !!detail?.immutable

  return (
    <Stack spacing={1}>
      <FilesTable
        files={files.map(response => response.data).filter(f => typeof f !== 'undefined')}
        emptyMessage={t('noFilesAttached')}
        disabled={immutable}
        FileAction={({ file, disabled }: { file: FileShape; disabled?: boolean }) => (
          <Tooltip title={t('removeFile')}>
            <span>
              <IconButton disabled={disabled || immutable} onClick={() => handleRemoveFile(file)}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      />

      {remainingFileCount > 0 && !immutable && (
        <>
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddExistingModal({ open: true })}
              disabled={immutable}
            >
              {t('addExistingFile')}
            </Button>
          </Box>

          <div {...getRootProps()}>
            <input {...getInputProps()} />

            <DashedDropzone
              onClick={open}
              sx={{ width: '100%' }}
              variant="outlined"
              style={{ backgroundColor: isDragActive ? '#aaffaa55' : undefined }}
              disabled={immutable}
              data-testid="dropzone-box"
            >
              <Stack spacing={1} alignItems="center">
                <CloudUploadIcon style={{ fontSize: 44 }} />
                <Typography>{t('dropOrClick', { count: remainingFileCount })}</Typography>
              </Stack>
            </DashedDropzone>
          </div>
        </>
      )}

      {params?.allowNoFiles && (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={detailValue?.fileResIds?.length === 0}
                onChange={(_, value) => (value ? saveDetail([]) : removeDetail())}
              />
            }
            label={params?.noFilesMessage ?? t('willNotUploadFiles')}
            disabled={detailValue?.fileResIds?.length > 0 || immutable}
          />
        </FormGroup>
      )}

      {!!helperText && (
        <Markdown typographyProps={{ variant: 'caption' }}>
          {engagement.data?.replaceCopyTokens(helperText)}
        </Markdown>
      )}

      <AddExistingFileModal
        {...addExistingModal}
        onClose={() => setAddExistingModal({ open: false })}
        handleAddExisting={handleAddExisting}
        extensions={extensions}
        maxSize={maxSize}
      />
    </Stack>
  )
}
